import { useEffect, useState } from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../constants";
import { lineName, lineQty, peso, poNumber, qtyLabel } from "./common";

/**
 * Price per packaging unit of the items of a P.O., set or corrected before or
 * after it is received (e.g. received without the prices).
 */
export default function PricesModal({ order, toggle, onSubmit, isLoading }) {
  const [prices, setPrices] = useState({}); // { itemId: price }

  useEffect(() => {
    if (!order) return;
    setPrices(
      order.items.reduce((result, { _id, price }) => {
        result[_id] = price ?? "";
        return result;
      }, {}),
    );
  }, [order]);

  if (!order) return null;

  // denied items (approved 0) are not delivered, so they have no price
  const items = order.items.filter(({ qnty }) => qnty.approved !== 0);
  const total = items.reduce(
    (sum, item) => sum + (Number(prices[item._id]) || 0) * lineQty(item),
    0,
  );

  return (
    <MDBModal isOpen={!!order} toggle={toggle} backdrop size="lg">
      <MDBModalHeader
        toggle={toggle}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="tags" className="mr-2" />
        Prices of {poNumber(order)}
      </MDBModalHeader>
      <MDBModalBody>
        <MDBTable small bordered className="mb-0">
          <thead className="grey lighten-4">
            <tr>
              <th>Item</th>
              <th style={{ width: 170 }}>Quantity</th>
              <th style={{ width: 140 }}>Price / unit</th>
              <th style={{ width: 130 }} className="text-right">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const generic = GenericProducts.find(item.pgid);
              return (
                <tr key={item._id}>
                  <td className="align-middle">
                    {generic && (
                      <code className="mr-1">{generic.abbreviation}</code>
                    )}
                    <b>{lineName(item)}</b>
                  </td>
                  <td className="small align-middle">
                    {qtyLabel(lineQty(item), item)}
                    {item.freeItem > 0 && (
                      <span className="d-block text-success">
                        + {item.freeItem} free
                      </span>
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      className="form-control form-control-sm"
                      placeholder="0.00"
                      value={prices[item._id] ?? ""}
                      title={`Price per ${item.unit || "unit"}`}
                      onChange={({ target }) =>
                        setPrices((prev) => ({
                          ...prev,
                          [item._id]: target.value,
                        }))
                      }
                    />
                  </td>
                  <td className="align-middle text-right">
                    {peso((Number(prices[item._id]) || 0) * lineQty(item))}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={3} className="text-right">
                Total
              </th>
              <th className="text-right">{peso(total)}</th>
            </tr>
          </tfoot>
        </MDBTable>
      </MDBModalBody>
      <MDBModalFooter className="py-1">
        <MDBBtn size="sm" color="light" onClick={toggle}>
          Close
        </MDBBtn>
        <MDBBtn
          size="sm"
          color="primary"
          disabled={isLoading}
          onClick={() =>
            onSubmit({
              _id: order._id,
              items: items.map(({ _id }) => ({
                _id,
                price: Number(prices[_id]) || 0,
              })),
            })
          }
        >
          <MDBIcon icon="save" className="mr-1" /> Save prices
        </MDBBtn>
      </MDBModalFooter>
    </MDBModal>
  );
}
